import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ExclamationTriangleIcon,
  TrashIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  Cog6ToothIcon,
  ServerIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useCleanupOrphanedDataMutation } from '../../store/api/superAdminApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';

const DataCleanup = () => {
  const [cleanupReport, setCleanupReport] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [cleanupOrphanedData, { isLoading }] = useCleanupOrphanedDataMutation();

  const handlePreviewCleanup = async () => {
    try {
      const result = await cleanupOrphanedData({ confirmDelete: false }).unwrap();
      setCleanupReport(result.data);
    } catch (error) {
      console.error('Preview cleanup failed:', error);
    }
  };

  const handleExecuteCleanup = async () => {
    try {
      const result = await cleanupOrphanedData({ confirmDelete: true }).unwrap();
      setCleanupReport(result.data);
      setShowConfirmDialog(false);
    } catch (error) {
      console.error('Execute cleanup failed:', error);
    }
  };

  const getSeverityColor = (orphanedCount, totalRecords) => {
    const percentage = (orphanedCount / totalRecords) * 100;
    if (percentage >= 50) return 'text-red-600 bg-red-50';
    if (percentage >= 25) return 'text-orange-600 bg-orange-50';
    return 'text-yellow-600 bg-yellow-50';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg mr-4">
              <ServerIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Data Cleanup</h1>
              <p className="text-gray-600 mt-1">Clean up orphaned data that references non-existent companies</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <ShieldCheckIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-blue-900">Super Admin Only</h3>
                <p className="text-sm text-blue-700 mt-1">
                  This tool helps maintain database integrity by removing data that references companies that no longer exist.
                  Use with caution as deleted data cannot be recovered.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Cleanup Actions</h2>
          <div className="flex flex-wrap gap-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handlePreviewCleanup}
              disabled={isLoading}
              className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <EyeIcon className="h-5 w-5 mr-2" />
              Preview Orphaned Data
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowConfirmDialog(true)}
              disabled={!cleanupReport || isLoading}
              className="flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <TrashIcon className="h-5 w-5 mr-2" />
              Execute Cleanup
            </motion.button>
          </div>
        </div>

        {/* Results */}
        {cleanupReport && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Cleanup Report</h2>
              {cleanupReport.deletedRecords > 0 && (
                <div className="flex items-center text-green-600">
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  {cleanupReport.deletedRecords} records deleted
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{cleanupReport.totalOrphanedRecords}</div>
                <div className="text-sm text-blue-700">Total Orphaned Records</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{cleanupReport.collections.filter(c => c.orphanedCount > 0).length}</div>
                <div className="text-sm text-green-700">Affected Collections</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{cleanupReport.deletedRecords || 0}</div>
                <div className="text-sm text-red-700">Records Deleted</div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Collection Details</h3>
              {cleanupReport.collections
                .filter(collection => collection.orphanedCount > 0 || collection.error)
                .map((collection, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <ServerIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <h4 className="font-medium text-gray-900 capitalize">
                            {collection.collection.replace(/([A-Z])/g, ' $1').toLowerCase()}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {collection.totalRecords || 0} total records
                          </p>
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${getSeverityColor(collection.orphanedCount, collection.totalRecords || 1)}`}>
                        {collection.orphanedCount} orphaned
                      </div>
                    </div>

                    {collection.error && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                        <div className="flex items-center">
                          <XCircleIcon className="h-4 w-4 text-red-600 mr-2" />
                          <span className="text-sm text-red-700">{collection.error}</span>
                        </div>
                      </div>
                    )}

                    {collection.orphanedRecords && collection.orphanedRecords.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-600 mb-2">Sample orphaned records:</p>
                        <div className="bg-gray-50 rounded p-2 text-xs font-mono">
                          {collection.orphanedRecords.map((record, idx) => (
                            <div key={idx} className="truncate">
                              ID: {record._id} | CompanyID: {record.companyId}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </motion.div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <LoadingSpinner />
            <p className="text-gray-600 mt-4">Processing cleanup request...</p>
          </div>
        )}

        {/* Confirmation Dialog */}
        {showConfirmDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6"
            >
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Confirm Data Cleanup</h3>
              </div>

              <p className="text-gray-600 mb-6">
                This action will permanently delete {cleanupReport?.totalOrphanedRecords || 0} orphaned records
                from {cleanupReport?.collections.filter(c => c.orphanedCount > 0).length || 0} collections.
                This action cannot be undone.
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmDialog(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExecuteCleanup}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Data
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataCleanup;